import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { StackSimple, CircleNotch, Download, FilePdf, CheckCircle } from "@phosphor-icons/react";

export const BatchConvert = () => {
  const [cmds, setCmds] = useState([]);
  const [sel, setSel] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [resumable, setResumable] = useState(null);
  const cancelRef = useRef(false);

  const loadCmds = async () => {
    if (cmds.length) return;
    try { const d = await api.clientCommands(); setCmds(d.commands || []); }
    catch { toast.error("Could not load client commands."); }
  };

  const toggle = (c) => setSel((s) => s.includes(c) ? s.filter((x) => x !== c) : (s.length >= 15 ? (toast.error("Max 15 commands per run"), s) : [...s, c]));

  const poll = async (job_id, t, windowMs) => {
    const started = Date.now();
    while (Date.now() - started < windowMs) {
      for (let w = 0; w < 2500 && !cancelRef.current; w += 200) { await new Promise((r) => setTimeout(r, 200)); }
      if (cancelRef.current) return;
      const s = await api.generationStatus(job_id);
      if (s.progress) setProgress(s.progress);
      if (s.status === "done") { setResult(s.result); setResumable(null); toast.success("Conversion complete", { id: t }); return; }
      if (s.status === "cancelled") { if (s.result) setResult(s.result); setResumable(job_id); toast.message("Conversion cancelled — partial results kept", { id: t }); return; }
      if (s.status === "error") { toast.error(s.error || "Conversion failed", { id: t }); return; }
    }
    toast.error("Conversion timed out.", { id: t });
  };

  const cancel = async () => {
    cancelRef.current = true;
    setBusy(false);
    setResumable(jobId);
    toast.message("Conversion cancelled");
    if (jobId) { try { await api.cancelJob(jobId); } catch (err) { console.debug("cancelJob failed (best-effort)", err); } }
  };

  const run = async () => {
    if (!sel.length) { toast.error("Select at least one command."); return; }
    setBusy(true); setResult(null); setProgress(null); setResumable(null); cancelRef.current = false;
    const t = toast.loading(`Converting ${sel.length} command(s) to Civil 3D…`);
    try {
      const { job_id } = await api.batchConvert({ commands: sel, platform: "AutoCAD Civil 3D", version: "2024" });
      setJobId(job_id);
      await poll(job_id, t, 280000);
    } catch { toast.error("Conversion failed", { id: t }); }
    finally { setBusy(false); }
  };

  const runAll = async (resumeId) => {
    setBusy(true); setResult(null); setProgress({ done: 0, total: cmds.length }); setResumable(null); cancelRef.current = false;
    const t = toast.loading(resumeId ? "Resuming conversion…" : "Converting ALL client commands to Civil 3D (this can take a while)…");
    try {
      const { job_id } = await api.convertAll({ platform: "AutoCAD Civil 3D", version: "2024", resume_job_id: resumeId || undefined });
      setJobId(job_id);
      await poll(job_id, t, 1500000);
    } catch { toast.error("Conversion failed", { id: t }); }
    finally { setBusy(false); }
  };

  const filtered = cmds.filter((c) => c.toLowerCase().includes(q.toLowerCase()));

  return (
    <Dialog onOpenChange={(o) => o && loadCmds()}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="batch-convert-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]">
          <StackSimple size={14} weight="bold" /> Batch Convert
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-2xl rounded-[4px]" data-testid="batch-convert-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><StackSimple size={18} weight="bold" className="text-[var(--primary)]" />Batch Convert — client commands → Civil 3D</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">Convert selected client commands (or all of them) to Civil 3D with a change-log and PDF report.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${cmds.length} commands…`} data-testid="batch-search" className="h-8 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-xs" />
          <span className="text-[11px] font-mono text-[var(--text-dim)] shrink-0">{sel.length}/15</span>
          <Button size="sm" onClick={run} disabled={busy || !sel.length} data-testid="batch-run-btn" className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white shrink-0">
            {busy ? <CircleNotch size={14} className="animate-spin" /> : null} Convert
          </Button>
          <Button size="sm" variant="outline" onClick={() => runAll()} disabled={busy} data-testid="convert-all-btn" className="h-8 rounded-[3px] text-xs gap-1.5 border-[var(--primary)] text-[var(--primary)] bg-transparent hover:bg-[var(--primary)]/10 shrink-0">
            Convert All
          </Button>
          {resumable && !busy && (
            <Button size="sm" onClick={() => runAll(resumable)} data-testid="convert-resume-btn" className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--success)]/90 hover:bg-[var(--success)] text-black shrink-0">
              Resume
            </Button>
          )}
        </div>
        {progress && busy && (
          <div className="text-[11px] font-mono text-[var(--text-dim)]" data-testid="batch-progress">
            <div className="flex items-center justify-between">
              <span>Converting {progress.done}/{progress.total} commands…</span>
              <button onClick={cancel} data-testid="batch-cancel-btn"
                className="px-2 py-0.5 rounded-[3px] border border-[var(--alert)]/60 text-[var(--alert)] hover:bg-[var(--alert)]/10">Cancel</button>
            </div>
            <div className="mt-1 h-1.5 bg-[var(--surface-2)] rounded-full overflow-hidden">
              <div className="h-full bg-[var(--primary)] transition-all" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
            </div>
          </div>
        )}
        <ScrollArea className="h-[180px] border border-[var(--border)] rounded-[3px] p-1">
          <div className="flex flex-wrap gap-1.5">
            {filtered.map((c) => (
              <button key={c} onClick={() => toggle(c)} data-testid={`batch-cmd-${c}`}
                className={`px-2 py-1 rounded-[3px] text-[11px] font-mono border transition-colors ${sel.includes(c) ? "border-[var(--primary)] bg-[var(--primary)]/15 text-[var(--primary)]" : "border-[var(--border)] text-[var(--text-dim)] hover:text-[var(--text)]"}`}>
                {c}
              </button>
            ))}
          </div>
        </ScrollArea>
        {result && (
          <div className="border border-[var(--border)] rounded-[3px] bg-[var(--surface-2)]" data-testid="batch-result">
            <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border)]">
              <span className="text-[12px] font-mono flex items-center gap-1.5 text-[var(--success)]"><CheckCircle size={14} weight="fill" />{(result.results || []).length} command(s) converted</span>
              <div className="flex gap-1.5">
                <a href={api.downloadUrl(result.id, "primary")}><Button size="sm" className="h-7 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"><Download size={13} />.lsp</Button></a>
                <a href={api.manualUrl(result.id)} data-testid="batch-report-btn"><Button size="sm" variant="ghost" className="h-7 rounded-[3px] text-xs gap-1.5"><FilePdf size={13} />Report (PDF)</Button></a>
              </div>
            </div>
            <ScrollArea className="h-[150px]"><pre className="text-[11px] font-mono p-3 whitespace-pre-wrap text-[var(--text)]/90" data-testid="batch-changelog">{result.change_log || "No change log."}</pre></ScrollArea>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
